import React, { useCallback, useEffect, useState } from 'react'
import { itemsInCell } from './lib/geometry'
import { PALETTES, TONE, BODY_FONT, HEAD_FONT } from './lib/palette'
import { secondaryBtn, dangerBtn } from './lib/styles'
import { listReflections, createReflection, updateReflection, deleteReflection, isUnauthorized } from './lib/api'
import SectorMap from './components/SectorMap'
import Drawer from './components/Drawer'
import ReflectionList from './components/ReflectionList'
import ReflectionForm, { emptyForm } from './components/ReflectionForm'

const NARROW_BREAKPOINT = 900
const RESIZE_DEBOUNCE_MS = 150

const NowMap = ({ user, onSignOut, onSessionExpired }) => {
    const [items, setItems] = useState([])
    const [loadState, setLoadState] = useState('loading') // 'loading' | 'error' | 'ready'
    const [panel, setPanel] = useState(null)
    const [isNarrow, setIsNarrow] = useState(typeof window !== 'undefined' && window.innerWidth < NARROW_BREAKPOINT)
    const [form, setForm] = useState(emptyForm(null))
    const [initialForm, setInitialForm] = useState(null)
    const [formError, setFormError] = useState(null)
    const [confirmDiscard, setConfirmDiscard] = useState(null) // null | 'close' | 'back'

    const loadItems = useCallback(() => {
        setLoadState('loading')
        listReflections()
            .then(data => {
                if (!Array.isArray(data)) throw new Error('unexpected response shape')
                setItems(data)
                setLoadState('ready')
            })
            .catch(err => {
                // An expired session is not a load failure. Showing "check your connection"
                // to someone who simply needs to sign in again sends them to fix the wrong
                // thing.
                if (isUnauthorized(err)) {
                    onSessionExpired()
                    return
                }
                console.error('Failed to load reflections:', err)
                setLoadState('error')
            })
    }, [onSessionExpired])

    useEffect(() => {
        loadItems()
    }, [loadItems])

    useEffect(() => {
        let timer = null
        const onResize = () => {
            if (timer) clearTimeout(timer)
            timer = setTimeout(() => setIsNarrow(window.innerWidth < NARROW_BREAKPOINT), RESIZE_DEBOUNCE_MS)
        }
        window.addEventListener('resize', onResize)
        return () => {
            if (timer) clearTimeout(timer)
            window.removeEventListener('resize', onResize)
        }
    }, [])

    const palette = PALETTES[TONE]

    const openList = cell => {
        setPanel({ mode: 'list', cell })
        setFormError(null)
        setConfirmDiscard(null)
    }
    const openForm = (mode, cell, item, formValues) => {
        setPanel({ mode, cell, item })
        setForm(formValues)
        setInitialForm(formValues)
        setFormError(null)
        setConfirmDiscard(null)
    }
    const addToCell = () => openForm('add', panel.cell, undefined, emptyForm(panel.cell))
    //const openAddDefault = () => openForm('add', null, undefined, emptyForm(null))
    const editItem = (item, cell) =>
        openForm('edit', cell, item, {
            formSide: item.side,
            formRing: item.ring,
            formValence: item.valence,
            formLevel: item.level,
            formTitle: item.title,
            formNote: item.note,
            formDate: item.date,
        })

    const closePanel = () => {
        setPanel(null)
        setFormError(null)
        setConfirmDiscard(null)
    }
    const backOrClose = () => {
        const cell = panel && panel.cell
        setFormError(null)
        setConfirmDiscard(null)
        setPanel(cell ? { mode: 'list', cell } : null)
    }

    const isListMode = !!panel && panel.mode === 'list'
    const isFormMode = !!panel && (panel.mode === 'add' || panel.mode === 'edit')
    const isDirty = isFormMode && !!initialForm && JSON.stringify(form) !== JSON.stringify(initialForm)

    // Close requested via the Close button, the overlay, or Escape.
    const requestClose = () => {
        if (confirmDiscard) {
            setConfirmDiscard(null) // treat a repeat close request as "keep editing"
            return
        }
        if (isDirty) {
            setConfirmDiscard('close')
            return
        }
        closePanel()
    }
    // Cancel requested from within the form (goes back to the list when there is one).
    const requestCancel = () => {
        if (isDirty) {
            setConfirmDiscard('back')
            return
        }
        backOrClose()
    }
    const resolveDiscard = () => {
        if (confirmDiscard === 'close') closePanel()
        else backOrClose()
    }

    const updateForm = updater => {
        setFormError(null)
        setForm(updater)
    }

    const deleteItem = async () => {
        const item = panel.item
        try {
            await deleteReflection(item.id)
            setItems(existing => existing.filter(i => i.id !== item.id))
            backOrClose()
        } catch (err) {
            if (isUnauthorized(err)) {
                onSessionExpired()
                return
            }
            console.error('Failed to delete reflection:', err)
            setFormError('Could not delete this reflection. Please try again.')
        }
    }

    const saveForm = async () => {
        if (!form.formTitle.trim()) {
            setFormError('Please add a title before saving.')
            return
        }
        const cell = panel.cell
        const payload = {
            side: form.formSide,
            ring: form.formRing,
            valence: form.formValence,
            level: form.formLevel,
            title: form.formTitle,
            note: form.formNote,
            date: form.formDate,
        }
        try {
            if (panel.mode === 'edit') {
                const updated = await updateReflection(panel.item.id, payload)
                setItems(existing => existing.map(i => (i.id === updated.id ? updated : i)))
            } else {
                const created = await createReflection(payload)
                setItems(existing => [...existing, created])
            }
        } catch (err) {
            if (isUnauthorized(err)) {
                onSessionExpired()
                return
            }
            console.error('Failed to save reflection:', err)
            setFormError('Could not save this reflection. Please try again.')
            return
        }
        setFormError(null)
        setConfirmDiscard(null)
        setPanel(cell ? { mode: 'list', cell } : null)
    }

    let drawerKicker = ''
    if (isListMode) drawerKicker = 'Reflections'
    else if (isFormMode) drawerKicker = panel.mode === 'edit' ? 'Edit reflection' : 'New reflection'

    const styles = {
        root: {
            minHeight: '100vh',
            background: palette.bg,
            fontFamily: BODY_FONT,
            color: palette.ink,
            position: 'relative',
            overflowX: 'hidden',
            '--sector-focus': palette.ink,
        },
        main: { maxWidth: '900px', margin: '0 auto', padding: '0 5px 60px' },
        header: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: '20px',
            flexWrap: 'wrap',
            padding: '44px 4px 8px',
        },
        title: { fontFamily: HEAD_FONT, fontWeight: 600, fontSize: '32px', margin: '0 0 6px' },
        tagline: { margin: 0, color: palette.sub, fontSize: '15px', maxWidth: '360px', lineHeight: 1.5 },
        addButton: {
            fontFamily: BODY_FONT,
            fontSize: '14px',
            fontWeight: 500,
            background: palette.ink,
            color: palette.panelBg,
            border: 'none',
            borderRadius: '999px',
            padding: '12px 20px',
            cursor: 'pointer',
            height: 'fit-content',
        },
        headerActions: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '10px' },
        accountRow: { display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' },
        accountEmail: { fontSize: '13px', color: palette.sub, maxWidth: '220px', overflowWrap: 'anywhere' },
        signOutBtn: {
            fontFamily: BODY_FONT,
            fontSize: '12px',
            background: 'none',
            border: 'none',
            color: palette.sub,
            cursor: 'pointer',
            padding: 0,
            textDecoration: 'underline',
        },
        errorBanner: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '12px',
            flexWrap: 'wrap',
            margin: '12px 4px 0',
            padding: '12px 16px',
            borderRadius: '10px',
            border: `1px solid oklch(78% 0.08 ${palette.negHue})`,
            background: `oklch(96% 0.02 ${palette.negHue})`,
            color: `oklch(38% 0.12 ${palette.negHue})`,
            fontSize: '14px',
        },
        confirmBox: {
            border: `1px solid ${palette.line}`,
            borderRadius: '10px',
            padding: '12px 14px',
            marginBottom: '16px',
            background: palette.bg,
        },
        confirmText: { fontSize: '14px', fontWeight: 500, margin: '0 0 10px' },
        confirmActions: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
    }

    const centerSub = loadState === 'loading' ? 'Loading…' : `${items.length} reflections`

    return (
        <div style={styles.root}>
            <div style={styles.main}>
                <header style={styles.header}>
                    <div>
                        <h1 style={styles.title}>Now Map</h1>
                    </div>
                    <div style={styles.headerActions}>
                        <div style={styles.accountRow}>
                            <span style={styles.accountEmail}>{user ? user.email : ''}</span>
                            <button style={styles.signOutBtn} onClick={onSignOut}>
                                Sign out
                            </button>
                        </div>
                    </div>
                </header>
                {loadState === 'error' && (
                    <div style={styles.errorBanner} role="alert">
                        <span>Could not load your reflections. Check your connection and try again.</span>
                        <button style={secondaryBtn(palette)} onClick={loadItems}>
                            Retry
                        </button>
                    </div>
                )}
                <SectorMap
                    palette={palette}
                    items={items}
                    selectedCell={panel ? panel.cell : null}
                    onSelectCell={openList}
                    centerSub={centerSub}
                />
            </div>
            {!!panel && (
                <Drawer palette={palette} isNarrow={isNarrow} kicker={drawerKicker} onRequestClose={requestClose}>
                    {isFormMode && confirmDiscard && (
                        <div style={styles.confirmBox} role="alert">
                            <p style={styles.confirmText}>Discard unsaved changes?</p>
                            <div style={styles.confirmActions}>
                                <button style={dangerBtn(palette)} onClick={resolveDiscard}>
                                    Discard
                                </button>
                                <button style={secondaryBtn(palette)} onClick={() => setConfirmDiscard(null)}>
                                    Keep editing
                                </button>
                            </div>
                        </div>
                    )}
                    {isListMode && (
                        <ReflectionList
                            palette={palette}
                            cell={panel.cell}
                            items={itemsInCell(items, panel.cell)}
                            onEdit={item => editItem(item, panel.cell)}
                            onAdd={addToCell}
                        />
                    )}
                    {isFormMode && (
                        <ReflectionForm
                            palette={palette}
                            form={form}
                            onFormChange={updateForm}
                            mode={panel.mode}
                            error={formError}
                            itemTitle={panel.item ? panel.item.title : ''}
                            onSave={saveForm}
                            onCancel={requestCancel}
                            onDelete={deleteItem}
                        />
                    )}
                </Drawer>
            )}
        </div>
    )
}

export default NowMap
